import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import classNames from "classnames";
import React, { createContext, useContext } from "react";

export interface PiChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  /** Leading visual: an avatar (28px), an icon (18px) or a thumbnail (24px). */
  icon?: React.ReactNode;
  /** Nothing chosen yet: dashed border, faded label. */
  isPlaceholder?: boolean;
  /** Its menu or dialog is open. */
  isOpen?: boolean;
  /**
   * `entity` (default) for who/what chips: bold label, tight left edge for an avatar.
   * `setting` for how chips (animation, bubble style): regular label, even padding.
   */
  tone?: "entity" | "setting";
  /** Size to the content instead of the fixed 184px (e.g. in a single-row toolbar). */
  fit?: boolean;
  /**
   * Replaces the chevron. Chips that open a dialog rather than a menu (the
   * outfit picker) end in an icon for that dialog, so they don't promise a list.
   */
  trailingIcon?: React.ReactNode;
}

/**
 * Chips inside this scope are neutral grey (scene-column grey text, grey border);
 * the open state keeps its blue ring. Used where blue is kept for state only
 * (take actor settings, Design Studio actor-settings-layout/d-*).
 */
const PiChipNeutralContext = createContext(false);

/** True inside PiChipNeutralScope: for controls styled to match the chips around them. */
export function usePiChipNeutral() {
  return useContext(PiChipNeutralContext);
}

export function PiChipNeutralScope(props: { children: React.ReactNode }) {
  return <PiChipNeutralContext.Provider value>{props.children}</PiChipNeutralContext.Provider>;
}

/**
 * The fixed-width chip trigger from the reworked interaction previews
 * (Design Studio `lookat-rework`). Every chip is 184px wide so rows line up.
 * Presentational only: PiChipSelect wraps it in a menu, other pickers open a dialog.
 */
const PiChip = React.forwardRef<HTMLButtonElement, PiChipProps>(function PiChip(
  { label, icon, isPlaceholder, isOpen, tone = "entity", fit, trailingIcon, className, disabled, ...buttonProps },
  ref
) {
  const neutral = useContext(PiChipNeutralContext);
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      {...buttonProps}
      className={classNames(
        "inline-flex flex-none items-center h-[40px] box-border overflow-hidden",
        fit ? "w-auto max-w-[240px]" : "w-[184px]",
        "rounded-[6px] border border-solid bg-creator-surface whitespace-nowrap outline-none transition-colors",
        "focus-visible:border-interaction-primary",
        {
          "gap-2 pl-1.5 pr-2.5 text-[14px]": tone === "entity",
          "gap-1.5 px-2.5 text-[13px]": tone === "setting",
          "font-semibold": tone === "entity" && !isPlaceholder,
          "font-medium": tone === "setting" || isPlaceholder,
          "border-interaction-primary-faded text-interaction-primary": !isPlaceholder && !neutral,
          "border-creator-border text-creator-text-sub": !isPlaceholder && neutral,
          "border-dashed text-creator-text-faded": isPlaceholder,
          "border-interaction-primary-faded": isPlaceholder && !neutral,
          "border-creator-border": isPlaceholder && neutral,
          "cursor-pointer hover:border-interaction-primary": !disabled && !isOpen && !neutral,
          "cursor-pointer hover:border-creator-text-sub": !disabled && !isOpen && neutral,
          "border-interaction-primary shadow-[0_0_0_3px_var(--pi-game-blue-ring)]": isOpen,
          "opacity-60 cursor-not-allowed": disabled,
        },
        className
      )}
    >
      {icon}
      <span className="min-w-0 truncate">{label}</span>
      {trailingIcon ? (
        <span className="ml-auto flex flex-none opacity-80">{trailingIcon}</span>
      ) : (
        <KeyboardArrowDownIcon
          className={classNames("ml-auto flex-none opacity-80 transition-transform", {
            "rotate-180": isOpen,
          })}
          sx={{ fontSize: 16 }}
        />
      )}
    </button>
  );
});

export default PiChip;
